import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import Swal from "sweetalert2";

import request from "../../../../Helpers/API";

const hoyISO = () => new Date().toISOString().slice(0, 10);

const ESTATUS_OPCIONES = [
  { codigo: "P", texto: "Pendiente" },
  { codigo: "R", texto: "Realizada" },
];

const FORM_VACIO = { idcliente: "", fecha: hoyISO(), tipo_visita: "", idlinea: "", estatus: ESTATUS_OPCIONES[0].codigo, notas: "" };

// Convierte la fila real (mdl_Listado_Visitas_Programadas) al formulario de edición.
// El campo "visita" trae el texto del tipo de visita (no el id) — se resuelve buscando el id
// correspondiente en el mismo catálogo (opcionesTipoVisita) que llena el <select>.
const mapVisitaToForm = (visita, opcionesTipoVisita) => ({
  idcliente: visita.idcliente,
  fecha: visita.fecha_visita ? new Date(visita.fecha_visita).toISOString().slice(0, 10) : hoyISO(),
  tipo_visita: opcionesTipoVisita.find((o) => o.valor === visita.visita)?.id ?? "",
  idlinea: visita.idlinea || "",
  estatus: visita.estatus || ESTATUS_OPCIONES[0].codigo,
  notas: visita.notas || "",
});

// Modal de alta/edición de visita — conectado a VisitasCRM/ProgramarVisita.
// El vendedor se captura automáticamente con el usuario en sesión (no es editable).
// Si clienteFijo llega (pantalla abierta desde CRM_Dashboard con un cliente ya buscado), el
// alta queda limitada a ese cliente: sin buscador y sin posibilidad de elegir otro.
export const VisitasModalNueva = ({ open, onClose, visita, clientes = [], opcionesTipoVisita = [], opcionesLineas = [], idvendedor, clienteFijo }) => {
  const esEdicion = !!visita;
  const [form, setForm] = useState(FORM_VACIO);
  const [busquedaCliente, setBusquedaCliente] = useState("");
  const [mostrarClientes, setMostrarClientes] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (esEdicion) {
      setForm(mapVisitaToForm(visita, opcionesTipoVisita));
    } else {
      setForm({ ...FORM_VACIO, idcliente: clienteFijo ? clienteFijo.idcliente : "" });
    }
    setBusquedaCliente("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visita]);

  const clienteSeleccionado = clientes.find((c) => String(c.idcliente) === String(form.idcliente)) || clienteFijo || null;

  const resultadosCliente = mostrarClientes
    ? clientes.filter((c) => (c.razon_social || "").toLowerCase().includes(busquedaCliente.toLowerCase()))
    : [];

  const handleSeleccionarCliente = (c) => {
    setForm((prev) => ({ ...prev, idcliente: c.idcliente }));
    setBusquedaCliente("");
    setMostrarClientes(false);
  };

  const handleQuitarCliente = () => setForm((prev) => ({ ...prev, idcliente: "" }));

  const handleChange = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  const validar = () => {
    if (!form.idcliente) { Swal.fire("VISITAS", "Selecciona un cliente", "warning"); return false; }
    if (!form.fecha) { Swal.fire("VISITAS", "La fecha es requerida", "warning"); return false; }
    if (!form.tipo_visita) { Swal.fire("VISITAS", "Selecciona el tipo de visita", "warning"); return false; }
    return true;
  };

  const handleGuardar = () => {
    if (!validar()) return;

    const body = {
      idvisita: esEdicion ? visita.id_visita : null,
      idcliente: Number(form.idcliente),
      usuario: 0,
      tipo_visita: Number(form.tipo_visita),
      linea: form.idlinea ? Number(form.idlinea) : null,
      fecha_visita: form.fecha,
      estatus: form.estatus,
      notas: form.notas,
    };

    setGuardando(true);
    request
      .post("VisitasCRM/ProgramarVisita", body)
      .then(async () => {
        setGuardando(false);
        await Swal.fire("VISITAS", `Visita ${esEdicion ? "actualizada" : "programada"} con éxito`, "success");
        onClose(true);
      })
      .catch((error) => {
        setGuardando(false);
        Swal.fire("VISITAS", JSON.stringify(error), "error");
      });
  };

  return (
    <Modal open={open} onClose={() => onClose(false)}>
      <div className="crm-modal-card crm-modal-card-lg crm-modal-card-overflow-visible">
        <button type="button" className="crm-modal-cerrar" onClick={() => onClose(false)}>
          <CloseIcon fontSize="small" />
        </button>

        <h3 className="crm-modal-titulo">{esEdicion ? "Editar visita" : "Nueva visita"}</h3>

        <div className="crm-modal-body">
          <div className="crm-field">
            <label>Cliente</label>
            {clienteSeleccionado ? (
              <div className="crm-detalle-cliente-asociado">
                <div>
                  <span className="asociado-nombre">{clienteSeleccionado.razon_social}</span>
                  <span className="asociado-id mono">{clienteSeleccionado.rfc}</span>
                </div>
                {!esEdicion && !clienteFijo && (
                  <button type="button" className="btn-quitar-asociado" onClick={handleQuitarCliente}>×</button>
                )}
              </div>
            ) : (
              <div className="crm-home-search-section">
                <div className={`crm-home-search-bar${resultadosCliente.length > 0 ? " search-open" : ""}`}>
                  <SearchIcon className="crm-search-icon" />
                  <input
                    type="text"
                    className="crm-search-input"
                    placeholder="Buscar cliente..."
                    value={busquedaCliente}
                    onChange={(e) => setBusquedaCliente(e.target.value)}
                    onFocus={() => setMostrarClientes(true)}
                    onBlur={() => setTimeout(() => setMostrarClientes(false), 150)}
                    autoComplete="off"
                  />
                </div>

                {resultadosCliente.length > 0 && (
                  <div className="crm-search-dropdown">
                    {resultadosCliente.map((c) => (
                      <div
                        key={c.idcliente}
                        className="crm-search-result"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSeleccionarCliente(c)}
                      >
                        <div className="crm-res-avatar"><AccountCircleOutlinedIcon /></div>
                        <div className="crm-res-info">
                          <span className="crm-res-nombre">{c.razon_social}</span>
                          <span className="crm-res-meta">
                            <BadgeIcon /> {c.rfc}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="vp-row2">
            <div className="crm-field">
              <label>Fecha</label>
              <input className="crm-field-editable" type="date" value={form.fecha} onChange={handleChange("fecha")} />
            </div>
            <div className="crm-field">
              <label>Tipo de visita</label>
              <select className="crm-field-editable" value={form.tipo_visita} onChange={handleChange("tipo_visita")}>
                <option value="">Seleccionar</option>
                {opcionesTipoVisita.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.valor}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="vp-row2">
            <div className="crm-field">
              <label>Línea</label>
              <select className="crm-field-editable" value={form.idlinea} onChange={handleChange("idlinea")}>
                <option value="">Seleccionar</option>
                {opcionesLineas.map((opt) => (
                  <option key={opt.idlinea} value={opt.idlinea}>{opt.linea}</option>
                ))}
              </select>
            </div>
            <div className="crm-field">
              <label>Estatus</label>
              <select
                className="crm-field-editable"
                value={form.estatus}
                onChange={handleChange("estatus")}
                disabled={esEdicion}
                title={esEdicion ? "El estatus se cambia desde la acción de estatus del listado" : undefined}
              >
                {ESTATUS_OPCIONES.map((opt) => (
                  <option key={opt.codigo} value={opt.codigo}>{opt.texto}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="crm-field">
            <label>{esEdicion ? "Comentario" : "Motivo de visita"}</label>
            <textarea
              className="crm-field-editable crm-modal-textarea"
              rows={3}
              placeholder="Detalles de la visita, temas a tratar..."
              value={form.notas}
              onChange={handleChange("notas")}
            />
          </div>
        </div>

        <div className="crm-modal-buttons">
          <button type="button" className="btn-secundario-outline" onClick={() => onClose(false)}>Cancelar</button>
          <button type="button" className="btn-primario-solido" onClick={handleGuardar} disabled={guardando}>Guardar visita</button>
        </div>
      </div>
    </Modal>
  );
};
